// src\components\dashboard\TVDashboard.jsx
import React, { useState, useEffect } from 'react';
import { Clock, Calendar, Activity } from 'lucide-react';
import EnhancedProductionCalendar from '../common/EnhancedProductionCalendar';
import TVBatchDetail from './TVBatchDetail';
import ResourceStatusPanel from '../common/ResourceStatusPanel';
import EmergencyAlert from '../common/EmergencyAlert';
import { useData } from '../../contexts/DataContext';

/**
 * Dashboard optimisé pour l'affichage sur téléviseur grand format
 * Conçu pour une visibilité maximale et sans interaction utilisateur
 */
const TVDashboard = () => {
  const { pendingOrders, batchAssignments, resources, stats, loading, error } = useData();
  
  // État pour la date sélectionnée (fixée au 31/03/2025 par défaut)
  const [selectedDate, setSelectedDate] = useState(new Date('2025-03-31'));
  
  // État pour les fournées à afficher
  const [visibleBatches, setVisibleBatches] = useState([]);
  
  // État pour l'alerte d'urgence (simulée)
  const [emergencyAlert, setEmergencyAlert] = useState(null);
  
  // Horloge pour la vue TV
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Générer les créneaux horaires pour le calendrier
  const generateTimeSlots = () => {
    const slots = [];
    const date = new Date(selectedDate);
    
    // Générer les créneaux pour 3 jours à partir de la date sélectionnée
    for (let d = 0; d < 5; d++) {
      const currentDate = new Date(date);
      currentDate.setDate(currentDate.getDate() + d);
      
      const formattedDate = currentDate.toLocaleDateString('fr-FR', { 
        weekday: 'long', 
        day: 'numeric', 
        month: 'long' 
      });
      
      slots.push(
        { id: `${d}-matin`, name: 'Matin', date: formattedDate },
        { id: `${d}-aprem`, name: 'Après-midi', date: formattedDate },
        { id: `${d}-nuit`, name: 'Nuit', date: formattedDate }
      );
    }
    
    return slots;
  };
  
  // Créneaux horaires pour le calendrier
  const timeSlots = generateTimeSlots();

  // Ajouter timeSlotId aux fournées pour l'affichage
  const batchesWithTimeSlots = batchAssignments.map(batch => {
    // Adapter les heures pour correspondre aux créneaux
    const hour = parseInt(batch.startTime.split(':')[0]);
    let period;
    
    if (hour >= 6 && hour < 14) {
      period = 'matin';
    } else if (hour >= 14 && hour < 22) {
      period = 'aprem';
    } else {
      period = 'nuit';
    }
    
    // Déterminer le jour à partir de la date
    const today = new Date(selectedDate);
    today.setHours(0, 0, 0, 0);
    
    const batchDateParts = batch.date.split('/');
    const batchDate = new Date(`${batchDateParts[2]}-${batchDateParts[1]}-${batchDateParts[0]}`);
    batchDate.setHours(0, 0, 0, 0);
    
    // Calculer la différence en jours
    const diffInDays = Math.floor((batchDate - today) / (1000 * 60 * 60 * 24));
    
    // Vérifier si la date est dans la plage de 3 jours
    if (diffInDays >= 0 && diffInDays <= 2) {
      const timeSlotId = `${diffInDays}-${period}`;
      return { ...batch, timeSlotId };
    } else {
      return null;
    }
  }).filter(batch => batch !== null);
  
  // Fonction pour formater la date pour l'affichage
  const formatDateRange = () => {
    const startDate = new Date(selectedDate);
    const endDate = new Date(selectedDate);
    endDate.setDate(endDate.getDate() + 2);
    
    return `${startDate.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long' })} - ${endDate.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}`;
  };
  
  // Trouver les fournées en cours
  const getCurrentBatches = () => {
    // Déterminer la date actuelle (simulée pour la démo)
    const currentDate = new Date('2025-03-31T10:30:00');
    
    return batchAssignments.filter(batch => {
      // Obtenir la date et les heures de début/fin de la fournée
      const batchDateParts = batch.date.split('/');
      const batchDate = new Date(`${batchDateParts[2]}-${batchDateParts[1]}-${batchDateParts[0]}`);
      
      // Vérifier si c'est aujourd'hui
      const isToday = batchDate.getDate() === currentDate.getDate() &&
                     batchDate.getMonth() === currentDate.getMonth() &&
                     batchDate.getFullYear() === currentDate.getFullYear();
      
      if (!isToday) return false;
      
      // Convertir les heures de début et de fin en minutes
      const [startHour, startMinute] = batch.startTime.split(':').map(Number);
      const [endHour, endMinute] = batch.endTime.split(':').map(Number);
      
      const batchStartMinutes = startHour * 60 + startMinute;
      const batchEndMinutes = endHour * 60 + endMinute;
      
      // Obtenir l'heure actuelle en minutes
      const currentHour = currentDate.getHours();
      const currentMinute = currentDate.getMinutes();
      const currentMinutes = currentHour * 60 + currentMinute;
      
      // Vérifier si l'heure actuelle est dans la plage de la fournée
      return currentMinutes >= batchStartMinutes && currentMinutes <= batchEndMinutes;
    });
  };
  
  // Fournées en cours pour l'affichage
  const currentBatches = getCurrentBatches();
  
  // Au chargement, sélectionner les fournées à afficher
  useEffect(() => {
    if (batchAssignments.length > 0) {
      // D'abord afficher les fournées en cours
      let batchesToShow = [...currentBatches];
      
      // Si pas assez de fournées en cours, ajouter des prochaines fournées
      if (batchesToShow.length < 4) {
        const upcomingBatches = batchAssignments
          .filter(batch => {
            // Exclure les fournées déjà sélectionnées
            return !batchesToShow.some(b => b.batchNumber === batch.batchNumber);
          })
          .sort((a, b) => {
            // Trier par date et heure de début
            const dateA = a.date.split('/').reverse().join('');
            const dateB = b.date.split('/').reverse().join('');
            
            if (dateA !== dateB) return dateA.localeCompare(dateB);
            return a.startTime.localeCompare(b.startTime);
          })
          .slice(0, 4 - batchesToShow.length);
        
        batchesToShow = [...batchesToShow, ...upcomingBatches];
      }
      
      setVisibleBatches(batchesToShow);
    }
  }, [batchAssignments]);
  
  // Mise à jour automatique de l'heure
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    
    return () => clearInterval(interval);
  }, []);
  
  // Simulation d'une alerte d'urgence après 10 secondes
  useEffect(() => {
    const timeout = setTimeout(() => {
      // Simuler une alerte sur l'Autoclave 2
      setEmergencyAlert({
        level: 'high',
        title: 'Alerte technique sur Autoclave 2',
        message: 'Une intervention technique est nécessaire suite à une anomalie de pression. La maintenance a été contactée.',
        resource: 'Autoclave 2',
        estimatedResolution: '14h30',
        contact: 'Service Maintenance (poste 4268)'
      });
    }, 10000);
    
    return () => clearTimeout(timeout);
  }, []);
  
  // Si les données sont en cours de chargement
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-500"></div>
      </div>
    );
  }
  
  // Si une erreur s'est produite
  if (error) {
    return (
      <div className="flex justify-center items-center h-screen bg-red-50">
        <div className="bg-white border-l-4 border-red-500 text-red-700 p-8 rounded-lg shadow-lg">
          <h1 className="text-2xl font-bold mb-4">Erreur de chargement des données</h1>
          <p className="text-xl">{error}</p>
          <p className="mt-4">Veuillez contacter le service informatique au poste 4200.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-100">
      {/* En-tête avec horloge et informations */}
      <header className="bg-blue-800 text-white py-3">
        <div className="flex justify-between items-center px-6">
          <div className="flex items-center">
            {/* Logo */}
            <img src="/logo_bleu.png" alt="Logo" className="h-14 mr-3" />
            <div>
              <h1 className="text-2xl font-bold">Planification des Moyens Chauds</h1>
              <div className="flex items-center mt-1">
                <Calendar size={18} className="mr-2" />
                <span className="text-lg">{formatDateRange()}</span>
              </div>
            </div>
          </div>
          
          {/* Horloge */}
          <div className="flex flex-col items-end">
            <div className="text-3xl font-bold">
              {currentTime.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
            </div>
            <div className="text-xl">
              {currentTime.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </div>
          </div>
        </div>
      </header>
      
      {/* Grille principale */}
      <div className="flex-grow grid grid-cols-1 lg:grid-cols-12 gap-4 p-4 overflow-hidden">
        {/* Colonne de gauche (8/12) - Calendrier et panneau d'état */}
        <div className="lg:col-span-8 flex flex-col space-y-4 overflow-hidden">
          {/* Calendrier de production */}
          <div className="flex-grow overflow-auto">
            <EnhancedProductionCalendar 
              resources={resources}
              timeSlots={timeSlots}
              batchAssignments={batchesWithTimeSlots}
              readOnly={true}
            />
          </div>
          
          {/* Panneau d'état des ressources */}
          <div className="h-64">
            <ResourceStatusPanel 
              resources={resources}
              currentBatches={currentBatches}
            />
          </div>
        </div>
        
        {/* Colonne de droite (4/12) - Détails des fournées */}
        <div className="lg:col-span-4 grid grid-rows-2 gap-4 overflow-hidden">
          {visibleBatches.slice(0, 2).map((batch, index) => (
            <div key={batch.batchNumber} className="overflow-auto">
              <TVBatchDetail 
                batch={batch}
                orders={pendingOrders.filter(order => order.assignedTo === batch.batchNumber)}
                resource={resources.find(r => r.id === batch.resourceId)}
              />
            </div>
          ))}
        </div>
      </div>
      
      {/* Alerte d'urgence (si présente) */}
      {emergencyAlert && (
        <EmergencyAlert 
          alert={emergencyAlert}
          onClose={() => setEmergencyAlert(null)}
        />
      )}
    </div>
  );
};

export default TVDashboard;